/**
 * DeckCardLibrary — shared App Deck UI for Single / Demo / Listening Together / Season.
 * User view: Available / Purchased tabs + talent filter (when logged in).
 * Purchased tab: card-thumbnail with purchased=true for every type.
 * Talent view: owned cards list.
 */

import { Box, Container, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";

import { useAuth } from "@/shared/stores/useAuth";
import useThemeStore from "@/shared/stores/useThemeStore";
import type { DeckCardKakaoKind } from "@/modules/kakao";

import { DeckCardLibraryProps, DeckCardType } from "@/cards/common/types";
import PublishedCardPreviewModal from "../preview-modal/PublishedCardPreviewModal";
import SingleCardListTalent from "@/cards/single-album-card/component/SingleCardListTalent";
import DemoCardUserPage from "@/cards/demo-card/component/card-preview/DemoCardUserPage";
import DemoCardTalentPage from "@/cards/demo-card/component/card-preview/DemoCardTalentPage";
import SeasonCardUserPage from "@/cards/season-card/component/card-preview/SeasonCardUserPage";
import SeasonCardTalentPage from "@/cards/season-card/component/card-preview/SeasonCardTalentPage";
import { useDeckCardLibrary } from "../../hooks/useDeckCardLibrary";
import PageHeader from "../PageHeader";
import DeckCardGrid from "./DeckCardGrid";
import CardFilters from "../CardFilters";
import OwnedDeckCardList from "./OwnedDeckCardList";
import {
  LISTENING_TOGETHER_CARD_NAVIGATION,
  SINGLE_CARD_NAVIGATION,
  type OwnedDeckCardNavigationConfig,
} from "@/cards/common/utils/app-deck/ownedDeckCardList.utils";
import { useDeckCardTalentExpandShare } from "./useDeckCardTalentExpandShare";

const PURCHASABLE_DECKS = new Set<DeckCardType>([
  "Single_Card",
  "Demo_Card",
  "Listening_Together_Card",
  "Season_Card",
]);

const DECK_TAB_PARAM: Partial<Record<DeckCardType, string>> = {
  Single_Card: "single_tab",
  Demo_Card: "demo_tab",
  Listening_Together_Card: "listening_together_tab",
  Season_Card: "season_tab",
};

const DECK_SHARE_KIND: Partial<Record<DeckCardType, DeckCardKakaoKind>> = {
  Single_Card: "single",
  Demo_Card: "demo",
  Season_Card: "season",
};

const DECK_NAVIGATION: Partial<Record<DeckCardType, OwnedDeckCardNavigationConfig>> = {
  Single_Card: SINGLE_CARD_NAVIGATION,
  Listening_Together_Card: LISTENING_TOGETHER_CARD_NAVIGATION,
};

const DECK_TITLE_KEY: Partial<Record<DeckCardType, string>> = {
  Single_Card: "single_card",
  Listening_Together_Card: "listening_together_card",
  Season_Card: "season_card",
};

function PurchasedDeckList({ cardType }: { cardType: DeckCardType }) {
  const { t } = useTranslation();
  const { userInfo } = useAuth();
  const shareKind = DECK_SHARE_KIND[cardType] ?? "single";
  const { onExpandShare, shareModal } = useDeckCardTalentExpandShare({
    cardKind: shareKind,
  });

  if (cardType === "Demo_Card") {
    return <DemoCardUserPage />;
  }

  if (cardType === "Season_Card") {
    return <SeasonCardUserPage />;
  }

  const titleDefault =
    cardType === "Listening_Together_Card" ? "Listening Together Card" : "Single Card";

  return (
    <>
      <OwnedDeckCardList
        deckCardType={cardType}
        viewMode="user"
        purchaseFilter="purchased"
        navigation={DECK_NAVIGATION[cardType] ?? SINGLE_CARD_NAVIGATION}
        tabParamKey={DECK_TAB_PARAM[cardType]}
        tabParamValue="purchased"
        titleLabel={t(DECK_TITLE_KEY[cardType] ?? "single_card", {
          ns: "taascard",
          defaultValue: titleDefault,
        })}
        emptyTitle={t("no_purchased_cards_title")}
        emptySubtitle={t("no_purchased_cards_subtitle")}
        showTalentFilter
        enabled={Boolean(userInfo?.id)}
        onExpandShare={onExpandShare}
        deckCardShareKind={shareKind}
      />
      {shareModal}
    </>
  );
}

function TalentDeckList({ cardType }: { cardType: DeckCardType }) {
  const { t } = useTranslation();
  const { userInfo } = useAuth();
  const shareKind = DECK_SHARE_KIND[cardType] ?? "single";
  const { onExpandShare, shareModal } = useDeckCardTalentExpandShare({
    cardKind: shareKind,
  });

  if (cardType === "Single_Card") {
    return <SingleCardListTalent />;
  }

  if (cardType === "Demo_Card") {
    return <DemoCardTalentPage />;
  }

  if (cardType === "Season_Card") {
    return <SeasonCardTalentPage />;
  }

  return (
    <>
      <OwnedDeckCardList
        deckCardType={cardType}
        viewMode="talent"
        purchaseFilter="all"
        navigation={DECK_NAVIGATION[cardType] ?? SINGLE_CARD_NAVIGATION}
        titleLabel={t("my_cards", { defaultValue: "My Cards" })}
        showTalentFilter={false}
        enabled={Boolean(userInfo?.id)}
        onExpandShare={onExpandShare}
        deckCardShareKind={shareKind}
      />
      {shareModal}
    </>
  );
}

const DeckCardLibrary: React.FC<DeckCardLibraryProps> = ({ cardType, title, subtitle }) => {
  const { t } = useTranslation();
  const { darkMode } = useThemeStore();
  const { userInfo } = useAuth();
  const isLoggedIn = Boolean(userInfo?.id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading,
    isUserView,
    filteredCards,
    uniqueTalents,
    thumbnailsByCardId,
    searchQuery,
    setSearchQuery,
    selectedTalentId,
    setSelectedTalentId,
    selectedCategory,
    selectedSubCategories,
    categoryData,
    handleCategoryClick,
    handleSubCategoryClick,
    selectedCard,
    setSelectedCard,
    isPreviewOpen,
    setIsPreviewOpen,
    expandCard,
    isExpandOpen,
    closeCardExpand,
    dismissExpandForDetails,
    openCardDetails,
    handleCardClick,
    removeIdFromUrl,
  } = useDeckCardLibrary(cardType);

  const tabParamKey = DECK_TAB_PARAM[cardType] ?? "single_tab";
  const tabParam = searchParams.get(tabParamKey);
  const [deckTab, setDeckTab] = useState(tabParam === "purchased" ? 1 : 0);
  const isPurchasableDeck = PURCHASABLE_DECKS.has(cardType);

  const previewCard = useMemo(() => {
    if (!selectedCard) return null;
    return {
      ...selectedCard,
      seal_time: selectedCard.seal_time ?? null,
    };
  }, [selectedCard]);

  useEffect(() => {
    const desired = !isLoggedIn ? 0 : tabParam === "purchased" ? 1 : 0;
    if (deckTab !== desired) setDeckTab(desired);
  }, [searchParams, isLoggedIn, tabParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeckTabChange = (_e: React.SyntheticEvent, v: number) => {
    setDeckTab(v);
    const next = new URLSearchParams(searchParams);
    next.set(tabParamKey, v === 1 ? "purchased" : "available");
    setSearchParams(next);
  };

  const purchasableTabSx = {
    "& .MuiTab-root": {
      textTransform: "none",
      fontFamily: '"Lexend", sans-serif',
      fontWeight: 600,
      color: darkMode ? "var(--neutral-400)" : "#6B7280",
      "&.Mui-selected": {
        color: darkMode ? "var(--primary-400)" : "var(--primary-600)",
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: darkMode ? "var(--primary-400)" : "var(--primary-600)",
    },
  };

  const activeTab = isLoggedIn ? deckTab : 0;

  const deckShareKind = DECK_SHARE_KIND[cardType] ?? "single";

  const deckExpandPortalProps = {
    open: isExpandOpen,
    onClose: closeCardExpand,
    onDismiss: dismissExpandForDetails,
    card: expandCard,
    thumbnail: expandCard ? (thumbnailsByCardId.get(expandCard.id) ?? null) : null,
    onOpenDetails: openCardDetails,
    deckCardShareKind: deckShareKind,
  };

  const renderBackButton = () => (
    <Box
      onClick={() => navigate("/TNPage")}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        fontWeight: 500,
        px: 1,
        py: 0.5,
        mb: 2.5,
        borderRadius: 2,
        transition: "all 0.2s ease",
        bgcolor: darkMode ? "var(--neutral-900)" : "var(--primary-100)",
        border: darkMode ? "1px solid var(--primary-400)" : "1px solid var(--primary-700)",
        "&:hover": {
          bgcolor: "action.hover",
          transform: "translateX(-2px)",
        },
      }}
    >
      <ArrowBack
        fontSize="small"
        sx={{ color: darkMode ? "var(--primary-400)" : "var(--primary-700)" }}
      />
      <Typography
        variant="body2"
        sx={{ color: darkMode ? "var(--primary-400)" : "var(--primary-700)" }}
      >
        {t("back")}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100%",
        position: "relative",
        overflow: "hidden",
        background: darkMode ? "var(--neutralBlack)" : "var(--neutral-100-light)",
        py: { xs: 2, sm: 4 },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 2 }}
      >
        {renderBackButton()}
        <PageHeader title={title} subtitle={subtitle} darkMode={darkMode} />

        {!isPurchasableDeck ? (
          <>
            <CardFilters
              darkMode={darkMode}
              loading={loading}
              cardType={cardType}
              filteredCount={filteredCards.length}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isUserView={isUserView}
              categoryData={categoryData}
              selectedCategory={selectedCategory}
              selectedSubCategories={selectedSubCategories}
              onCategoryClick={handleCategoryClick}
              onSubCategoryClick={handleSubCategoryClick}
              uniqueTalents={uniqueTalents}
              selectedTalentId={selectedTalentId}
              onTalentSelect={setSelectedTalentId}
            />
            <DeckCardGrid
              loading={loading}
              cards={filteredCards}
              cardType={cardType}
              darkMode={darkMode}
              searchQuery={searchQuery}
              onCardClick={handleCardClick}
              expandPortalProps={deckExpandPortalProps}
              thumbnailsByCardId={thumbnailsByCardId}
            />
          </>
        ) : isUserView ? (
          <>
            {isLoggedIn && (
              <Box sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={handleDeckTabChange} sx={purchasableTabSx}>
                  <Tab label={t("available_cards", "Available Cards")} />
                  <Tab label={t("purchased_cards", "Purchased Cards")} />
                </Tabs>
              </Box>
            )}

            {activeTab === 0 && (
              <>
                <CardFilters
                  darkMode={darkMode}
                  loading={loading}
                  cardType={cardType}
                  filteredCount={filteredCards.length}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isUserView={isUserView}
                  categoryData={categoryData}
                  selectedCategory={selectedCategory}
                  selectedSubCategories={selectedSubCategories}
                  onCategoryClick={handleCategoryClick}
                  onSubCategoryClick={handleSubCategoryClick}
                  uniqueTalents={uniqueTalents}
                  selectedTalentId={selectedTalentId}
                  onTalentSelect={setSelectedTalentId}
                />
                <DeckCardGrid
                  loading={loading}
                  cards={filteredCards}
                  cardType={cardType}
                  darkMode={darkMode}
                  searchQuery={searchQuery}
                  onCardClick={handleCardClick}
                  expandPortalProps={deckExpandPortalProps}
                  thumbnailsByCardId={thumbnailsByCardId}
                />
              </>
            )}

            {isLoggedIn && activeTab === 1 && <PurchasedDeckList cardType={cardType} />}
          </>
        ) : (
          <TalentDeckList cardType={cardType} />
        )}
      </Container>

      {activeTab === 0 && (
        <PublishedCardPreviewModal
          open={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedCard(null);
            removeIdFromUrl();
          }}
          card={previewCard}
        />
      )}
    </Box>
  );
};

export default DeckCardLibrary;
export type { DeckCardLibraryProps, DeckCardType } from "../../types";
